/**
 * 放大编辑弹窗（共用）：文本节点 / 图片节点 / 视频节点的提示词
 * 都用此弹窗进行放大编辑与审阅。portal 到 body 避开画布 transform。
 *
 * 文本节点传入 / 存储富文本 HTML；图片 / 视频节点传入纯文本、提交时
 * 由调用方将 HTML 转纯文本写回（prompt 保持纯文本存储）。
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RichTextEditor } from '../RichTextEditor';
import { sanitizeRichTextHtml, htmlToPlainText } from '../../utils/richTextBox';

interface TextEditorDialogProps {
  /** 弹窗标题 */
  title?: string;
  /** 初始内容（HTML 或纯文本，纯文本作为 HTML 传入同样安全） */
  initialValue: string;
  /** 占位提示 */
  placeholder?: string;
  /** 完成回调，参数为净化后的 HTML（只读模式可不传） */
  onCommit?: (html: string) => void;
  /** 关闭回调 */
  onClose: () => void;
  /** 只读模式：隐藏工具栏、禁用编辑、按钮改为关闭 */
  readOnly?: boolean;
}

export function TextEditorDialog({
  title = '放大编辑',
  initialValue,
  placeholder,
  onCommit,
  onClose,
  readOnly = false,
}: TextEditorDialogProps) {
  const [draft, setDraft] = useState(() => sanitizeRichTextHtml(initialValue));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCommit = () => {
    onCommit?.(sanitizeRichTextHtml(draft));
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-950/40 p-6"
      onClick={onClose}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex h-full max-h-[80vh] w-full max-w-3xl flex-col gap-3 rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <span className="text-xs tabular-nums text-slate-400">
            {htmlToPlainText(draft).length} 字
          </span>
        </div>
        <div
          className="flex min-h-0 flex-1 flex-col"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              handleCommit();
            }
          }}
        >
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            placeholder={placeholder ?? '输入文字内容，支持加粗 / 列表 / 标题等格式（Ctrl+Enter 完成）'}
            className="min-h-0 flex-1"
            readOnly={readOnly}
          />
        </div>
        <div className="flex shrink-0 justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            取消
          </button>
          {readOnly ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-blue-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-600"
            >
              关闭
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCommit}
              className="rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-600"
            >
              完成
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
